import { Link } from 'react-router-dom'
import { Layers3 } from 'lucide-react'

type LegalPageProps = {
  type: 'terms' | 'privacy' | 'refund'
}

const updatedAt = 'May 18, 2026'
const contactEmail = 'support@excelopdf.com'

const content = {
  terms: {
    title: 'Terms of Service',
    intro: 'These Terms of Service govern your access to and use of ExcelOPDF, including document upload, table extraction, preview, export, and subscription features.',
    sections: [
      ['Use of the service', 'You may use ExcelOPDF to upload supported documents, process tables, preview extracted data, and export selected results. You are responsible for ensuring that you have the right to upload and process each document.'],
      ['Accounts and authentication', 'Some features require an account. You agree to provide accurate account information and to keep your login method secure. We may suspend access if we detect abuse, fraud, or activity that threatens the platform.'],
      ['Subscriptions and billing', 'Paid plans may include higher usage limits, advanced scanning, priority processing, and additional workflow features. Prices, plan limits, and included features are shown on the pricing page before checkout.'],
      ['Uploaded content', 'You retain ownership of documents and extracted data that you upload or generate. You grant us the limited rights needed to store, process, preview, and export that content for your use of the service.'],
      ['Acceptable use', 'You may not upload malware, attempt to bypass limits, overload the service, reverse engineer protected systems, scrape private endpoints, or use the service for unlawful activity.'],
      ['Service availability', 'We work to keep the service reliable, but we do not guarantee uninterrupted availability. Features may change as the product evolves.'],
      ['Limitation of liability', 'To the maximum extent permitted by law, ExcelOPDF is provided as is and our liability is limited to the amount you paid for the service during the previous three months.'],
      ['Contact', `Questions about these terms can be sent to ${contactEmail}.`],
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    intro: 'This Privacy Policy explains how ExcelOPDF collects, uses, stores, and protects information when you use the service.',
    sections: [
      ['Information we collect', 'We collect account information such as email address and name from Google sign-in, uploaded document metadata, processing status, usage metrics, export records, and payment/subscription events from our payment provider.'],
      ['Documents and extracted data', 'Documents are processed to detect tables, generate previews, and create exports. We do not sell uploaded documents or extracted data. Access is limited to the authenticated user and operational systems needed to provide the service.'],
      ['How we use information', 'We use information to authenticate users, process documents, prevent duplicates, enforce limits, improve extraction quality, provide support, secure the platform, and manage subscriptions.'],
      ['Cookies', 'We use secure authentication cookies for login sessions. These cookies help keep users signed in and protect access to private documents.'],
      ['Third-party services', 'We may use Google authentication, payment providers, hosting providers, storage systems, and document processing infrastructure to operate the service. These providers process data only as needed for service delivery.'],
      ['Data retention', 'We retain account, document, and export records while your account is active or as needed for legal, billing, security, and operational purposes. You may request deletion where applicable.'],
      ['Security', 'We use access controls, authenticated downloads, duplicate detection, and infrastructure safeguards. No online service can guarantee perfect security, so sensitive documents should be uploaded only when appropriate for your workflow.'],
      ['Contact', `Privacy questions or deletion requests can be sent to ${contactEmail}.`],
    ],
  },
  refund: {
    title: 'Refund Policy',
    intro: 'This Refund Policy describes when paid subscriptions may be refunded.',
    sections: [
      ['Free evaluation', 'ExcelOPDF offers a free evaluation workflow so users can test document preview before purchasing a paid plan.'],
      ['Subscription refunds', 'If you purchased a paid plan and the service does not work as described for your use case, contact us within 14 days of the initial purchase. We will review the request and may issue a full or partial refund.'],
      ['Renewals', 'Renewal payments are generally non-refundable once a new billing period starts, except where required by law or where a billing error occurred.'],
      ['Usage-based limits', 'Refunds may be declined if the account has used a substantial portion of included processing, export, or advanced scan capacity during the billing period.'],
      ['Cancellations', 'You may cancel a paid subscription to stop future renewals. Cancellation does not automatically refund past payments unless this policy or applicable law requires it.'],
      ['How to request a refund', `Send your account email, payment date, and reason for the request to ${contactEmail}.`],
    ],
  },
} satisfies Record<LegalPageProps['type'], { title: string; intro: string; sections: [string, string][] }>

export default function LegalPage({ type }: LegalPageProps) {
  const page = content[type]

  return (
    <main className="min-h-screen bg-white text-gray-950">
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-brand-blue text-white grid place-items-center">
              <Layers3 className="w-5 h-5" />
            </div>
            <span className="text-xl font-bold">ExcelOPDF</span>
          </Link>
          <Link to="/" className="text-sm font-medium text-gray-600 hover:text-brand-blue">Back to home</Link>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-5 py-12">
        <p className="text-sm font-semibold text-brand-blue">Last updated: {updatedAt}</p>
        <h1 className="mt-3 text-4xl font-bold tracking-tight">{page.title}</h1>
        <p className="mt-4 text-lg text-gray-600 max-w-3xl">{page.intro}</p>

        <div className="mt-10 space-y-8">
          {page.sections.map(([title, text]) => (
            <section key={title} className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold">{title}</h2>
              <p className="mt-3 text-gray-700 leading-7">{text}</p>
            </section>
          ))}
        </div>
      </section>
    </main>
  )
}
